/**
 * Copyright (c) 2018-present, Luojun Jin
 *
 * @emails john.king.ca@gmail.com
 */

import React, {Fragment, PureComponent} from 'react';
// import * as queryString from 'query-string';
import ReactJson from 'react-json-view';
import GroupedVerseSelection from './GroupedVerseSelection';
import {toType} from 'utils/get-options';

/**
 * Module global variable, set in the recursion function during walking through kepan tree.
 */
let gSubKepanTree = {};

class GroupedKepanTree extends PureComponent {
  constructor(props) {
    super(props);
  }

  state = {
    src: this.props.originalKepanTree,
    name: this.props.name,
    selectedVerseOption: undefined,
    selectedVersePath: undefined,
  }

  static defaultProps = {
    collapseStringsAfter: 15,
    displayObjectSize: false,
    enableClipboard: false,
    indentWidth: 2,
    displayDataTypes: false,
    iconStyle: 'triangle',
  }

  /**
   * handle router query
   *  - if verse included, then display kepan
   */
  // componentDidMount() {
  //   const queryParams = queryString.parse(this.props.location.search);
  //   const verse = queryParams.verse;
  //   if (verse && verseList.includes(verse)) {
  //     this.showVerseKepan({value: verse, label: verse});
  //   }
  // }

  /**
   * get the sub json tree starting from the `start_name` along the path of the json tree
   *
   * @param {*} src the json tree node to be processed
   * @param {*} copy_namespace the path to the leaf of json tree, it is consumed by the function.
   * @param {*} start_name the name of start node.
   */
  searchKepanTree = (src, copy_namespace, start_name) => {
    let idx = copy_namespace.shift();
    const type = toType(src);
    let result;

    if (type == 'array') {
      result = [...src];
    } else if (type == 'object') {
      result = { ...src };
    }

    if (idx !== undefined) {
      /**
       * recursion termination case
       */
      if (idx.includes(start_name)) {
        gSubKepanTree = Object.assign({}, result[idx]);
        return gSubKepanTree;
      } else {
        result[idx] = this.searchKepanTree(src[idx], copy_namespace, start_name);
      }
    }

    return result;
  }

  /**
   * handle the event when user click the verse on the kepan tree direclty
   * it will
   *    1. do the default operation as react-json-view selection when the same verse is clicked
   *    2. otherwise, change the selected version option and reset the expansion depth
   *
   * An example parameters:
   *    name: `颂词`
   *    type: string
   *    value: `【心怀喜敬最胜信，除盖烦恼而离垢，行众生利寂静者，请听般若勇士行。】`
   *    namespace: `全论分二,甲一：依照直叙空性之理而解释（分4部分）,乙三：论义（分2部分）,丙二：真正论义（分3部分）,丁一：劝勉听法（分2部分）,戊一：真实劝听`
   */
  handleVerseClicked = ({ name, value, type, namespace }) => {
    if (this.state.selectedVerseOption) {
      if (value !== this.state.selectedVerseOption.value) {
        this.setState({selectedVerseOption: {label: value, value: value}});
        this.setState({selectedExpansionDepthOption: {}});
      }
    }
  }

  /**
   * show the sub kepan tree for selected verse, the depth of tree depends on
   *   - default depth
   *   - selected depth and
   *   - the depth of kepan tree
   */
  showVerseKepan = (selectedVerseOption) => {
    this.createKepanTree(selectedVerseOption.value);
  }

  /**
   * common code for showVerseKepan(...) and handleVerseClicked(...)
   */
  createKepanTree = (verse, depth?) => {
    const { DEFAULT_EXPANSION_DEPTH, kepanPaths, originalKepanTree } = this.props;
    // determine the namespace and source kepan tree
    let src = Object.assign({}, originalKepanTree);
    const verseOption = { label: verse, value: verse };

    let namespace = kepanPaths[verse].slice();
    // need to skip last path `颂词`, otherwise, we will get an `undefined`
    namespace.splice(-1, 1);

    // determine expansion depth based on current verse and its own depth.
    // selectedExpansionDepthOption: {label: ..., value: ... }
    depth = depth || (this.state.selectedExpansionDepthOption ? this.state.selectedExpansionDepthOption.value : DEFAULT_EXPANSION_DEPTH);

    // get root node name for the new created sub kepan tree
    let rootNodeName;
    if (namespace.length >= depth) {
      rootNodeName = namespace[namespace.length - depth];
    } else {
      rootNodeName = namespace[0];
    }

    // make a copy of namespace to avoid mutation inside function call.
    // we use module global variable gSubKepanTree to store the newly create sub kepan tree
    this.searchKepanTree(src, namespace.slice(), rootNodeName);
    this.setState({ src: gSubKepanTree, name: rootNodeName, selectedVerseOption: verseOption, selectedVersePath: namespace});
  }

  render() {
    // properties for styling rjv
    const {
      collapseStringsAfter,
      displayObjectSize,
      enableClipboard,
      theme,
      iconStyle,
      indentWidth,
      displayDataTypes,
    } = this.props;

    // properties from parents for json data
    const {groupedVerseOptions, defaultKepanPaths} = this.props;
    const { src, name, selectedVersePath, selectedVerseOption  } = this.state;

    const rjvStyle = {
      padding: '10px',
      borderRadius: '3px',
      margin: '10px 0px',
    };

    return (
      <Fragment>
        <GroupedVerseSelection
          selectedVerseOption={selectedVerseOption}
          groupedVerseOptions={groupedVerseOptions}
          createKepanTree={this.createKepanTree}
        />
        {selectedVerseOption ?
          (typeof window !== 'undefined' && ReactJson &&
          <ReactJson
            name={name}
            style={rjvStyle}
            theme={theme}
            src={src}
            collapseStringsAfterLength={collapseStringsAfter}
            onSelect={e => { this.handleVerseClicked(e); }}
            displayObjectSize={displayObjectSize}
            enableClipboard={enableClipboard}
            indentWidth={indentWidth}
            displayDataTypes={displayDataTypes}
            iconStyle={iconStyle}
            shouldCollapse={({ name, src, namespace, type }) => {
              if (selectedVersePath.includes(name)) {
                return false;
              } else {
                return true;
              }
            }}
          />
          )
          : (typeof window !== 'undefined' && ReactJson &&
          <ReactJson
            name={name}
            style={rjvStyle}
            theme={theme}
            src={src}
            collapseStringsAfterLength={collapseStringsAfter}
            onSelect={e => { this.handleVerseClicked(e); }}
            displayObjectSize={displayObjectSize}
            enableClipboard={enableClipboard}
            indentWidth={indentWidth}
            displayDataTypes={displayDataTypes}
            iconStyle={iconStyle}
            shouldCollapse={({ name, src, namespace, type }) => {
              if (defaultKepanPaths.includes(name)) {
                return false;
              } else {
                return true;
              }
            }}
          />
          )
        }
      </Fragment>
    );
  } // end of render

}

export default GroupedKepanTree;

